import { useEffect, useState } from 'react'
import { repositories } from '@/services/repositories'
import type { UserSettings } from '@/types'

export type BreathingSettingsSnapshot = {
  /** Raw settings row, null when unavailable (offline, not configured). */
  settings: UserSettings | null
  /** Soft sound cues only ever on an explicit opt-in. */
  soundsEnabled: boolean
  /** Haptic cues default on unless the user turned breathing vibration off. */
  hapticsEnabled: boolean
  /** In-app reduced motion preference (separate from the OS preference). */
  reducedMotionSetting: boolean
}

const FALLBACK: BreathingSettingsSnapshot = {
  settings: null,
  soundsEnabled: false,
  hapticsEnabled: true,
  reducedMotionSetting: false,
}

function toSnapshot(settings: UserSettings | null): BreathingSettingsSnapshot {
  if (!settings) return FALLBACK
  return {
    settings,
    soundsEnabled: settings.sounds === true,
    hapticsEnabled: settings.breathingVibration !== false,
    reducedMotionSetting: settings.reducedMotion === true,
  }
}

/**
 * Reads the user settings relevant to breathing cues via the profile
 * repository. Tolerates null rows and repository failures (e.g.
 * SUPABASE_NOT_CONFIGURED): the exercise flow stays usable with the
 * conservative fallbacks above.
 */
export function useBreathingSettings(): BreathingSettingsSnapshot {
  const [snapshot, setSnapshot] = useState<BreathingSettingsSnapshot>(FALLBACK)

  useEffect(() => {
    let cancelled = false
    repositories.profile
      .getSettings()
      .then((settings) => {
        if (!cancelled) setSnapshot(toSnapshot(settings))
      })
      .catch(() => {
        if (!cancelled) setSnapshot(FALLBACK)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return snapshot
}
